import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Typography, Chip, InputLabel, MenuItem, Grid, useTheme } from "@mui/material"
import { useApi } from "auth/useApi3"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { apiEndpoints } from "generated/apiEndpoints"
import ClpTable from "components/ClpTable"
import { TableLoadingStateEnum } from "components/ClpTable/tableProps.interface"
import TableLink from "components/ClpTable/components/actions/TableLink/TableLink"
import { allRoutes } from "generated/routes"
import { VehicleCheckHistory } from "./CheckHistoryTable.interfaces"
import { showDate, showDateTime } from "components/DateHandler/dateHandler"
import { StyledTableColumnSelect, StyledTableFormControl } from "shared/styled/General"
import { SwitchOption } from "../UpcomingChecksTab/UpcomingChecksTab.interfaces"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"
import { ViewToggler } from "components/ViewToggler/ViewToggler"
import ChipInfoIcon from "components/ChipInfo/ChipInfo"
import {
  faCalendarCircleExclamation,
  faCheckCircle,
  faClockRotateLeft
} from "@fortawesome/pro-solid-svg-icons"
import { NumberTypeSelector } from "components/ClpTable/components/NumberTypeSelector"

const tableDesc = `The table shows vehicles that have had their <strong>service or inspection completed</strong> and marked as approved or rejected.
Here you can track <strong>when and where the inspection was performed</strong>, view <strong>notes from the workshop</strong>, as well as the date the inspection was <strong>processed</strong>.
Use this view for follow-up and documentation of completed inspections.`

export const ServiceHistoryTable: React.FC<SwitchOption> = ({
  viewList,
  view,
  setView,
  params
}: SwitchOption) => {
  const [historyResult, historyApi] = useApi() as [IApiResult, IApiFunc]
  const { t } = useTranslation()
  const theme = useTheme()
  const [numberType, setNumberType] = useState("reg")

  function setProp<T extends Record<string, any>>(obj: T, path: string[], value: any): void {
    let current: any = obj

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i]
      if (!current[key] || typeof current[key] !== "object") {
        current[key] = {}
      }
      current = current[key]
    }

    const lastKey = path[path.length - 1]
    current[lastKey] = value
  }

  useEffect(() => {
    const url = apiEndpoints.vehicleregistrychecks.getdetailedvehiclecheckhistorybycustomer
    historyApi.get(url)
  }, [historyApi])

  const serviceHistory = useMemo(() => {
    if (historyResult.status !== 2) return []
    const filteredData = historyResult.data
      .filter(
        (item: VehicleCheckHistory) =>
          item.customFieldName === "NEW SERVICE FEATURE" || item.customFieldName === "NEW INSPECTION FEAUTURE"
      )
      ?.sort((a: any, b: any) => new Date(b?.createdTime).getTime() - new Date(a?.createdTime).getTime())

    if (!params?.id) return filteredData
    const number = Number(params.id)
    const filteredParamsData = filteredData.filter((item: VehicleCheckHistory) => item.vehicleId === number)
    return filteredParamsData
  }, [historyResult])

  const columns = useMemo(
    () => [
      {
        accessorKey: "checkTemplate.name",
        header: t("global.name", "Name"),
        cell: ({ cell }: any) => {
          const { checkTemplate } = cell.row.original
          if (!checkTemplate) return <Typography variant="bodySmall">-</Typography>

          return <Typography sx={{ fontWeight: 600 }}>{checkTemplate.name}</Typography>
        }
      },
      {
        accessorKey: numberType === "intern" ? "internNumber" : "regNumber",
        header:
          numberType === "intern"
            ? t("global.internal_number_short", "Intern No")
            : t("global.reg_number_short", "Reg No"),
        cell: ({ cell }: any) => {
          const row = cell.row.original
          const displayValue = numberType === "intern" ? row.internNumber : row.regNumber

          return row.vehicleId ? (
            <TableLink
              label={displayValue || "-"}
              to={allRoutes.VehicleregistryVehiclesDetails(row.vehicleId)}
            />
          ) : (
            <Typography>{displayValue || "-"}</Typography>
          )
        }
      },
      {
        accessorKey: "status",
        accessorFn: (row: VehicleCheckHistory) => {
          const status = row.status
          if (status === "Completed" || status === "Passed") return "Passed"
          if (status === "Pending" || status === "Waiting") return "Waiting"
          if (status === "Failed") return "Failed"
          return status ?? ""
        },
        header: t("global.status", "Status"),
        cell: ({ cell }: any) => {
          const { status } = cell.row.original as VehicleCheckHistory

          const chipConfig: any = {
            icon: {},
            text: {},
            chip: {},
            border: {}
          }

          let statusLabel = status

          if (status === "Completed" || status === "Passed") {
            statusLabel = t("global.passed", "Passed")
            setProp(chipConfig, ["icon", "source"], faCheckCircle)
            setProp(chipConfig, ["icon", "color"], theme.palette.success.dark)
            setProp(chipConfig, ["text", "color"], theme.palette.success.dark)
            setProp(chipConfig, ["chip", "backgroundColor"], (theme.palette as any).success[200])
            setProp(chipConfig, ["chip", "color"], "success")
            setProp(chipConfig, ["border", "color"], theme.palette.success.dark)
          } else if (status === "Failed") {
            statusLabel = t("global.failed", "Failed")
            setProp(chipConfig, ["icon", "source"], faCalendarCircleExclamation)
            setProp(chipConfig, ["icon", "color"], theme.palette.error.main)
            setProp(chipConfig, ["text", "color"], theme.palette.error.main)
            setProp(chipConfig, ["chip", "backgroundColor"], theme.palette.error.light)
            setProp(chipConfig, ["chip", "color"], "error")
            setProp(chipConfig, ["border", "color"], theme.palette.error.main)
          } else if (status === "Pending" || status === "Waiting") {
            statusLabel = t("global.waiting", "Waiting")
            setProp(chipConfig, ["icon", "source"], faClockRotateLeft)
            setProp(chipConfig, ["icon", "color"], theme.palette.warning.main)
            setProp(chipConfig, ["text", "color"], theme.palette.warning.main)
            setProp(chipConfig, ["chip", "backgroundColor"], theme.palette.warning.light)
            setProp(chipConfig, ["chip", "color"], "warning")
            setProp(chipConfig, ["border", "color"], theme.palette.warning.main)
          } else {
            return <Typography variant="bodySmall">{status}</Typography>
          }

          setProp(chipConfig, ["text", "data"], statusLabel)

          return (
            <ChipInfoIcon
              icon={chipConfig.icon}
              text={chipConfig.text}
              chip={chipConfig.chip}
              border={chipConfig.border}
            />
          )
        }
      },
      {
        accessorFn: (row: VehicleCheckHistory) => showDateTime(row?.attendanceDate),
        header: t("global.performed", "Performed")
      },
      {
        accessorKey: "odometerReading",
        header: `${t("global.meter_setting", "Meter Setting")} (km)`,
        cell: ({ cell }: any) => {
          const value = cell.row.original.odometerReading
          return value ? `${value.toLocaleString()} km` : "-"
        }
      },
      {
        accessorFn: (row: any) => {
          return row?.carWorkshop?.name || "-"
        },
        header: t("global.workshop", "Workshop")
      },
      {
        accessorKey: "notes",
        header: t("global.notes", "Notes"),
        cell: ({ cell }: any) => {
          const notes = cell.row.original.notes || cell.row.original.remark
          if (notes && notes.length > 50) {
            return (
              <Box title={notes}>
                <Typography variant="bodySmall" noWrap>
                  {notes.substring(0, 50)}...
                </Typography>
              </Box>
            )
          }
          return <Typography variant="bodySmall">{notes || "-"}</Typography>
        }
      },
      {
        accessorFn: (row: VehicleCheckHistory) => showDate(row?.createdTime),
        header: t("global.managed", "Handled")
      }
    ],
    [t, numberType]
  )

  return (
    <Box>
      <Box pt={3} px={3} display="flex" alignItems="center" gap={1} justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="labelSmall">{t("global.table_information", "Table Information")}</Typography>
          <DynamicTooltip t={t} title={t("global.table_information", "Table Information")}>
            <Grid item gap={1}>
              <Typography fontWeight="600" variant="labelSmall">
                {t("vehicleregistry.how_to_read_control_overview", "How to read the control overview")}
              </Typography>
              <Typography
                sx={{ mt: 0.5 }}
                variant="bodySmall"
                color={theme.palette.grey[600]}
                dangerouslySetInnerHTML={{
                  __html: t("checks.history_table_desc", tableDesc)
                }}
              />
            </Grid>
          </DynamicTooltip>
        </Box>
        <Box display="flex" gap={2} sx={{ flexWrap: "wrap" }}>
          <NumberTypeSelector
            value={numberType}
            onChange={setNumberType}
            label={t("global.show_reg_int_nr", "Show Reg. No./Internal No.")}
            options={[
              { value: "reg", label: t("global.reg_number_short", "Reg No") },
              { value: "intern", label: t("global.internal_number_short", "Intern No") }
            ]}
          />
          <ViewToggler view={view} setView={setView} viewList={viewList} />
        </Box>
      </Box>
      <ClpTable
        columns={columns}
        rows={serviceHistory}
        loadingState={historyResult.status as TableLoadingStateEnum}
      />
    </Box>
  )
}

export default ServiceHistoryTable
